import { combineReducers, configureStore, UnknownAction } from '@reduxjs/toolkit';
import authReducer from './slices/authSlice';
import planningPeriodsReducer from './slices/planningPeriodsSlice';
import investibleStructuresReducer from './slices/investibleStructuresSlice';
import programsReducer from './slices/programsSlice';
import teamsReducer from './slices/teamsSlice';
import epicsReducer from './slices/epicsSlice';
import featuresReducer from './slices/featuresSlice';
import milestonesReducer from './slices/milestonesSlice';
import okrsReducer from './slices/okrsSlice';
import scenariosReducer from './slices/scenariosSlice';
import dashboardReducer from './slices/dashboardSlice';
import dependenciesReducer from './slices/dependenciesSlice';
import workflowReducer from './slices/workflowSlice';
import reportsReducer from './slices/reportsSlice';
import bulkOperationsReducer from './slices/bulkOperationsSlice';
import usersReducer from './slices/usersSlice';
import notificationsReducer from './slices/notificationsSlice';
import isAllocationGridReducer from './slices/isAllocationGridSlice';
import timelineReducer from './slices/timelineSlice';
import divisionsReducer from './slices/divisionsSlice';
import resourcesReducer from './slices/resourcesSlice';
import resourceTeamAssignmentsReducer from './slices/resourceTeamAssignmentsSlice';
import rateCardsReducer from './slices/rateCardsSlice';
import resourceLevelsReducer from './slices/resourceLevelsSlice';
import resourceIntakeRequestsReducer from './slices/resourceIntakeRequestsSlice';
import allocationsReducer from './slices/allocationsSlice';
import matrixAllocationsReducer from './slices/matrixAllocationsSlice';
import costsReducer from './slices/costsSlice';
import searchReducer from './slices/searchSlice';
import featureReadinessReducer from './slices/featureReadinessSlice';
import featureAllocationsReducer from './slices/featureAllocationsSlice';

// Combine all reducers
const appReducer = combineReducers({
  auth: authReducer,
  planningPeriods: planningPeriodsReducer,
  investibleStructures: investibleStructuresReducer,
  programs: programsReducer,
  teams: teamsReducer,
  epics: epicsReducer,
  features: featuresReducer,
  milestones: milestonesReducer,
  okrs: okrsReducer,
  scenarios: scenariosReducer,
  dashboard: dashboardReducer,
  dependencies: dependenciesReducer,
  workflow: workflowReducer,
  reports: reportsReducer,
  bulkOperations: bulkOperationsReducer,
  users: usersReducer,
  notifications: notificationsReducer,
  isAllocationGrid: isAllocationGridReducer,
  timeline: timelineReducer,
  divisions: divisionsReducer,
  resources: resourcesReducer,
  resourceTeamAssignments: resourceTeamAssignmentsReducer,
  rateCards: rateCardsReducer,
  resourceLevels: resourceLevelsReducer,
  resourceIntakeRequests: resourceIntakeRequestsReducer,
  allocations: allocationsReducer,
  matrixAllocations: matrixAllocationsReducer,
  costs: costsReducer,
  search: searchReducer,
  featureReadiness: featureReadinessReducer,
  featureAllocations: featureAllocationsReducer,
});

// Root reducer that resets ALL state on logout
const rootReducer = (state: ReturnType<typeof appReducer> | undefined, action: UnknownAction) => {
  // Reset all state to initial values on logout
  if (action.type === 'auth/logout/fulfilled') {
    // Pass undefined to get initial state for all slices
    return appReducer(undefined, action);
  }
  return appReducer(state, action);
};

export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: ['persist/PERSIST', 'persist/REHYDRATE'],
      },
    }),
});

export type RootState = ReturnType<typeof appReducer>;
export type AppDispatch = typeof store.dispatch;
